class EconoSmartCartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.lastTrigger = null;
    this.termsAccepted = false;
    this.bindEvents();
    this.syncTermsState();
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-cart-open]');
      const close = event.target.closest('[data-cart-close]');
      const quickAdd = event.target.closest('[data-quick-add]');
      const change = event.target.closest('[data-cart-change]');

      if (open && window.EconoSmart?.cartBehavior === 'drawer') {
        event.preventDefault();
        this.open(open);
      }

      if (close) {
        event.preventDefault();
        this.close();
      }

      if (quickAdd) {
        event.preventDefault();
        this.quickAdd(quickAdd);
      }

      if (change) {
        event.preventDefault();
        this.changeLine(change);
      }
    });

    document.addEventListener('change', (event) => {
      const terms = event.target.closest('[data-cart-terms]');
      const note = event.target.closest('[data-cart-note]');

      if (terms) {
        this.termsAccepted = terms.checked;
        this.syncTermsState();
      }

      if (note) {
        this.updateNote(note.value);
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('.product-form');

      if (form && window.EconoSmart?.cartBehavior === 'drawer') {
        if (!event.submitter || event.submitter.name !== 'add') return;

        event.preventDefault();
        this.addProductForm(form, event.submitter);
        return;
      }

      const checkoutForm = event.target.closest('[data-cart-checkout-form]');
      if (checkoutForm && this.termsRequired() && !this.termsAccepted) {
        event.preventDefault();
        const terms = checkoutForm.querySelector('[data-cart-terms]');
        terms?.focus();
        this.announce('Debes aceptar los términos antes de finalizar la compra.');
      }
    });

    document.addEventListener('keydown', (event) => {
      if (!this.drawer?.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    });
  }

  termsRequired() {
    return this.drawer?.dataset.cartTermsRequired === 'true';
  }

  open(trigger = null) {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    if (trigger && !trigger.closest('[data-cart-drawer]')) {
      this.lastTrigger = trigger;
    }

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('drawer-open');
    this.syncTermsState();

    window.setTimeout(() => {
      this.drawer?.querySelector('[data-cart-close]')?.focus();
    }, 60);
  }

  close() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('drawer-open');

    this.termsAccepted = false;
    this.syncTermsState();

    if (this.lastTrigger?.isConnected) {
      this.lastTrigger.focus();
    }
  }

  trapFocus(event) {
    const panel = this.drawer?.querySelector('.cart-drawer__panel');
    if (!panel) return;

    const focusable = Array.from(
      panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  syncTermsState() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    const checkbox = this.drawer.querySelector('[data-cart-terms]');
    const checkout = this.drawer.querySelector('[data-cart-checkout]');

    if (!this.termsRequired()) {
      if (checkout) checkout.disabled = false;
      return;
    }

    if (checkbox) checkbox.checked = this.termsAccepted;
    if (checkout) checkout.disabled = !this.termsAccepted;
  }

  async quickAdd(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    const originalLabel = button.textContent;
    const triggerInsideDrawer = Boolean(button.closest('[data-cart-drawer]'));

    button.disabled = true;
    button.textContent = 'Agregando…';

    try {
      const useDrawer = window.EconoSmart?.cartBehavior === 'drawer';
      const payload = {
        items: [{ id: variantId, quantity: 1 }]
      };

      if (useDrawer) {
        payload.sections = 'cart-drawer';
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(window.EconoSmart.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'No se pudo agregar el producto.');

      if (!useDrawer) {
        window.location.href = window.EconoSmart.routes.cart;
        return;
      }

      this.replaceDrawer(data.sections?.['cart-drawer']);
      this.open(triggerInsideDrawer ? null : button);
      this.announce('Producto agregado al carrito.');
    } catch (error) {
      console.error('EconoSmart quick add failed:', error);
      button.textContent = 'No disponible';
      window.setTimeout(() => {
        if (button.isConnected) button.textContent = originalLabel;
      }, 1800);
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        if (button.textContent === 'Agregando…') button.textContent = originalLabel;
      }
    }
  }

  async addProductForm(form, submitter) {
    const button = submitter || form.querySelector('[name="add"]');
    const errorElement = form.querySelector('[data-product-form-error]');
    const originalLabel = button?.textContent;

    if (button) {
      button.disabled = true;
      button.textContent = 'Agregando…';
    }

    if (errorElement) errorElement.textContent = '';

    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(window.EconoSmart.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'No se pudo agregar el producto.');

      this.replaceDrawer(data.sections?.['cart-drawer']);
      this.open(button);
      this.announce('Producto agregado al carrito.');
    } catch (error) {
      console.error('EconoSmart add to cart failed:', error);
      if (errorElement) errorElement.textContent = error.message;
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  }

  async changeLine(button) {
    const line = Number(button.dataset.line);
    const quantity = Number(button.dataset.quantity);
    if (!line || Number.isNaN(quantity) || button.disabled) return;

    button.disabled = true;
    this.drawer?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(window.EconoSmart.routes.cartChange, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'No se pudo actualizar el carrito.');

      this.replaceDrawer(data.sections?.['cart-drawer']);
      this.open();
      this.announce('Carrito actualizado.');

      if (window.EconoSmart?.template === 'cart') window.location.reload();
    } catch (error) {
      console.error('EconoSmart cart update failed:', error);
      button.disabled = false;
      this.drawer?.removeAttribute('aria-busy');
    }
  }

  async updateNote(note) {
    const status = this.drawer?.querySelector('[data-cart-note-status]');
    if (status) status.textContent = 'Guardando…';

    try {
      const response = await fetch(window.EconoSmart.routes.cartUpdate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ note })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'No se pudo guardar la nota.');

      this.announce('Nota del pedido guardada.');
      if (status) status.textContent = 'Guardada';
    } catch (error) {
      console.error('EconoSmart cart note failed:', error);
      if (status) status.textContent = 'No se pudo guardar';
    }
  }

  replaceDrawer(html) {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incomingWrapper = parsed.querySelector('#shopify-section-cart-drawer');
    const currentWrapper = document.querySelector('#shopify-section-cart-drawer');

    if (incomingWrapper && currentWrapper) {
      currentWrapper.replaceWith(incomingWrapper);
    } else {
      const incomingDrawer = parsed.querySelector('[data-cart-drawer]');
      const currentDrawer = document.querySelector('[data-cart-drawer]');
      if (incomingDrawer && currentDrawer) currentDrawer.replaceWith(incomingDrawer);
    }

    this.drawer = document.querySelector('[data-cart-drawer]');
    this.syncTermsState();
    this.updateHeaderCount();
  }

  updateHeaderCount() {
    const count = Number(this.drawer?.dataset.cartItemCount || 0);

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.classList.toggle('is-empty', count === 0);
    });
  }

  announce(message) {
    const region = this.drawer?.querySelector('[data-cart-live-region]');
    if (region) region.textContent = message;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new EconoSmartCartDrawer();
});
